import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Spinner } from '@gsa/afp-component-library';
import { useQuery } from '@apollo/client';
import { PrivateRoute, useAppAbility } from '@gsa/afp-shared-ui-utils';
import GET_VENDORS from '../feature-toggle/get-vendors-query';
import CAN_I_ACCESS_BID from './queries';

const NonAdminControlledAccess = ({
  props: privateRouteProps,
  component: Component,
  bidId,
}) => {
  const {
    loading: vendorLoading,
    called: vendorCalled,
    data: vendorData,
  } = useQuery(GET_VENDORS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      operation: 'view',
      subject: 'Vendor',
    },
  });

  const {
    loading: bidAccessLoading,
    called: bidAccessCalled,
    data: bidAccessData,
  } = useQuery(CAN_I_ACCESS_BID, {
    fetchPolicy: 'cache',
    variables: { bidId },
    skip: !bidId,
  });

  if (vendorCalled && vendorLoading) {
    return (
      <Spinner
        data-testid="access-route-spinner"
        className="margin-top-4"
        size="medium"
      />
    );
  }

  const hasDraftInactiveVendors = vendorData?.getVendorsByPermission?.some(
    (vndr) => vndr.fleetStatus === 'Draft' || vndr.fleetStatus === 'Inactive',
  );

  if (hasDraftInactiveVendors) {
    return <Navigate to="/unauthorized" />;
  }

  if (bidId !== null) {
    if (bidAccessCalled && bidAccessLoading) {
      return (
        <Spinner
          data-testid="access-route-spinner"
          className="margin-top-4"
          size="medium"
        />
      );
    }

    const isBidAccessible = bidAccessData?.canIAccessThisBid;

    if (!isBidAccessible) {
      return <Navigate to="/unauthorized" />;
    }
  }

  return (
    <PrivateRoute {...privateRouteProps}>
      <Component />
    </PrivateRoute>
  );
};

const AccessControlledRoute = ({
  contextLocation,
  contextNavigation,
  component: Component,
  operation,
  title,
  validate,
  featureName,
  subject,
}) => {
  const ability = useAppAbility();
  const { bidId = null } = useParams();

  const privateRouteProps = {
    contextLocation,
    contextNavigation,
    title,
    validate,
    featureName,
  };

  if (operation) privateRouteProps.operation = operation;
  if (subject) privateRouteProps.subject = subject;

  if (ability === undefined || ability === null) {
    // render nothing until useAbility is done loading
    // then we decide what to do based on the ability
    return null;
  }

  if (ability.can('view', 'PSOQuestionnaire')) {
    // return <PrivateRoute {...rest} />;
    return (
      <PrivateRoute {...privateRouteProps}>
        <Component />
      </PrivateRoute>
    );
  }

  return (
    <NonAdminControlledAccess 
      props={privateRouteProps} 
      component={Component}
      bidId={bidId}
    />
  );
};

export default AccessControlledRoute;
